import type { AfterViewInit, ElementRef, OnInit } from '@angular/core';
import { Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import type { UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { isNullOrUndefined } from 'is-what';

@Component({
  standalone: false,
  selector: 'invd-fm-input-with-validations-for-request-an-exchange',
  templateUrl: './fm-input-with-validations-for-request-an-exchange.component.html',
  styleUrls: ['./fm-input-with-validations-for-request-an-exchange.component.scss'],
})
export class FmInputWithValidationsForRequestAnExchangeComponent implements OnInit, AfterViewInit {
  @Input()
  public isTraderSel = false;

  @Output()
  dealSearch = new EventEmitter<any>();

  public usrInput: string;
  public usrCUSIPInput: string;

  public form: UntypedFormGroup;

  public isSearchBtnEnable = false;
  public isCusipSearch = false;
  public isCusipFieldFocused = false;
  public isDealNbrFieldFocused = false;

  //for demo use only
  public mDealSubType = 'SFREMIC';
  public requestType = '';

  public showError = { CUSIP: '', dealNbr: false };

  @ViewChild('inputCusip', { static: true }) inputCusip: ElementRef;
  @ViewChild('inputDealNbr', { static: true }) inputDealNbr: ElementRef;

  public fb = inject(UntypedFormBuilder);

  ngOnInit() {
    this.form = this.fb.group({
      dealNbr: ['enter deal number', Validators.compose([this.validateDealNbr()])],
      CUSIP: ['enter cusip', Validators.compose([this.validateCusip()])],
    });
  }

  ngAfterViewInit() {
    this.isSearchBtnEnable = false;
  }

  public onDealSearch(): void {
    if (!this.isSearchBtnEnable) {
      return;
    } else {
      if (this.form.get('dealNbr').value !== undefined && this.form.get('dealNbr').value !== '') {
        this.getSubType({ value: this.form.get('dealNbr').value });
        this.dealSearch.emit({ dealSubType: this.mDealSubType, userSearchInput: this.usrInput });
      } else if (
        this.form.get('CUSIP').value !== undefined &&
        this.form.get('CUSIP').value !== ''
      ) {
        this.dealSearch.emit({
          dealSubType: this.mDealSubType,
          userSearchInput: this.usrCUSIPInput,
        });
      }
    }
  }

  private validateCusip(): ValidatorFn {
    return (control: UntypedFormControl): { [key: string]: boolean } => {
      if (!isNullOrUndefined(control.value)) {
        const cusipRegex = new RegExp('^[a-zA-Z0-9]{9}$');
        if (!cusipRegex.test(control.value)) {
          this.isSearchBtnEnable = false;
          return { cusipFormatError: false };
        } else {
          this.mDealSubType = '';
          this.isSearchBtnEnable = true;
          return null;
        }
      }
    };
  }

  public cancelCusipInput(): void {
    this.form.get('CUSIP').reset();
    this.form.get('CUSIP').markAsPristine();
    this.inputCusip.nativeElement.focus();
    this.form.get('dealNbr').enable();
  }

  private validateSearchNo(): ValidatorFn {
    function isEmptyInputValue(value: any) {
      return value == null || (typeof value === 'string' && value.length === 0);
    }
    return (control: UntypedFormControl): { [key: string]: boolean } => {
      if (isEmptyInputValue(control.value)) {
        return null;
      }
      if (control.dirty) {
        if (this.form.get('CUSIP').value !== undefined && this.form.get('CUSIP').value !== '') {
          const cusipRegex = new RegExp('^[a-zA-Z0-9]{9}$');
          if (cusipRegex.test(control.value)) {
            this.usrCUSIPInput = control.value;
            this.mDealSubType = '';
            this.isSearchBtnEnable = true;
            return { sfRemicError: true };
          } else {
            this.isSearchBtnEnable = false;
            return { sfRemicCUSIPError: true };
          }
        } else if (
          this.form.get('dealNbr').value !== undefined &&
          this.form.get('dealNbr').value !== ''
        ) {
          return this.getSubType(control);
        }
      } else {
        return { sfRemicError: true };
      }
    };
  }

  private getSubType(control: any): { [key: string]: boolean } {
    const dealRegex = new RegExp('^[0-9]{4}[-][0-9]{3}$');
    const dealSMBSRegex = new RegExp('^[0-9]{3}$');
    const dealMultiFamilyRegex = new RegExp('^[0-9]{4}[-][Mm][0-9A-Za-z]{2}$');
    const dealBenchMarkRegex = new RegExp('^[0-9]{4}[-][Bb][0-9]{2}$');
    const dealGrantorTrustRegex = new RegExp('^[0-9]{4}[-][Tt][0-9]{2}$');
    const dealWholeLoanRegex = new RegExp('^[0-9]{4}[-][Ww][0-9]{2}$');
    const dealGinneMaeRegex = new RegExp('^[0-9]{4}[-][Gg][0-9]{2}$');
    const dealSwapTrustRegex = new RegExp('^[0-9]{4}[-][Ss][0-9]{2}$');
    const dealPrivateLableRegex = new RegExp('^[0-9]{4}[-][Pp][0-9]{2}$');
    const dealCallableRegex = new RegExp('^[0-9]{4}[-][Cc][0-9]{2}$');
    const dealDebtDealRegex = new RegExp('^[0-9]{4}[-][Dd][0-9]{2}$');
    const dealRedeemableRegex = new RegExp('^[0-9]{4}[-][Rr][0-9]{2}$');

    if (dealRegex.test(control.value)) {
      this.requestType = 'Single Family REMIC';
    } else if (dealSMBSRegex.test(control.value)) {
      this.requestType = 'SMBS';
    } else if (dealMultiFamilyRegex.test(control.value)) {
      this.requestType = 'Multi Family REMIC';
    } else if (dealBenchMarkRegex.test(control.value)) {
      this.requestType = 'Benchmark REMIC';
    } else if (dealGrantorTrustRegex.test(control.value)) {
      this.requestType = 'Grantor Trust REMIC';
    } else if (dealWholeLoanRegex.test(control.value)) {
      this.requestType = 'Whole Loan REMIC';
    } else if (dealGinneMaeRegex.test(control.value)) {
      this.requestType = 'Ginnie Mae REMIC';
    } else if (dealSwapTrustRegex.test(control.value)) {
      this.requestType = 'SWAP Trust REMIC';
    } else if (dealPrivateLableRegex.test(control.value)) {
      this.requestType = 'Private Label REMIC';
    } else if (dealCallableRegex.test(control.value)) {
      this.requestType = 'Callable REMIC';
    } else if (dealDebtDealRegex.test(control.value)) {
      this.requestType = 'Debt Deal REMIC';
    } else if (dealRedeemableRegex.test(control.value)) {
      this.requestType = 'Redeemable REMIC';
    } else if (this.isCusipSearch) {
      this.requestType = 'Single Family REMIC';
    } else {
      this.requestType = '';
    }

    if (this.requestType === 'Single Family REMIC') {
      this.mDealSubType = 'SFREMIC';

      if (new RegExp('^[0-9]{4}[-][0-9]{3}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return {
          sfRemicError: false,
        };
      } else {
        this.isSearchBtnEnable = false;
        return { sfRemicError: true };
      }
    } else if (this.requestType === 'SMBS') {
      this.mDealSubType = 'SMBS';
      if (new RegExp('^[0-9]{3}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { smbsError: false };
      } else {
        return { smbsError: true };
      }
    } else if (this.requestType === 'Multi Family REMIC') {
      this.mDealSubType = 'MFREMIC';
      if (new RegExp('^[0-9]{4}[-][Mm][0-9A-Za-z]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { mfRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { mfRemicError: true };
          } else {
            return { mfRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'Benchmark REMIC') {
      this.mDealSubType = 'BMREMIC';
      if (new RegExp('^[0-9]{4}[-][Bb][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { bmRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { bmRemicError: true };
          } else {
            return { bmRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'Grantor Trust REMIC') {
      this.mDealSubType = 'GTREMIC';
      if (new RegExp('^[0-9]{4}[-][Tt][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { gtRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { gtRemicError: true };
          } else {
            return { gtRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'Whole Loan REMIC') {
      this.mDealSubType = 'WLREMIC';
      if (new RegExp('^[0-9]{4}[-][Ww][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { wlRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { wlRemicError: true };
          } else {
            return { wlRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'Ginnie Mae REMIC') {
      this.mDealSubType = 'GINNIE';
      if (new RegExp('^[0-9]{4}[-][Gg][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { gnRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { gnRemicError: true };
          } else {
            return { gnRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'SWAP Trust REMIC') {
      this.mDealSubType = 'STREMIC';
      if (new RegExp('^[0-9]{4}[-][Ss][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { stRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { stRemicError: true };
          } else {
            return { stRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'Private Label REMIC') {
      this.mDealSubType = 'PLREMIC';
      if (new RegExp('^[0-9]{4}[-][Pp][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { plRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { plRemicError: true };
          } else {
            return { plRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'Callable REMIC') {
      this.mDealSubType = 'CTREMIC';
      if (new RegExp('^[0-9]{4}[-][Cc][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { callableRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { callableRemicError: true };
          } else {
            return { callableRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'Debt Deal REMIC') {
      this.mDealSubType = 'DDREMIC';
      if (new RegExp('^[0-9]{4}[-][Dd][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { ddRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { ddRemicError: true };
          } else {
            return { ddRemicError: false };
          }
        }
      }
    } else if (this.requestType === 'Redeemable REMIC') {
      this.mDealSubType = 'RDMB';
      if (new RegExp('^[0-9]{4}[-][Rr][0-9]{2}$').test(control.value)) {
        this.isSearchBtnEnable = true;
        return { redeemRemicError: false };
      } else {
        this.isSearchBtnEnable = false;
        if (this.usrInput) {
          if (this.usrInput.length > 3) {
            return { redeemRemicError: true };
          } else {
            return { redeemRemicError: false };
          }
        }
      }
    } else {
      this.isSearchBtnEnable = false;
      return { sfRemicError: true };
    }
  }
  public toggleCusipDealerInputs(inputName: string): void {
    if (inputName === 'cusipField') {
      if (
        this.form.get('CUSIP').value === '' ||
        this.form.get('CUSIP').value === undefined ||
        this.form.get('CUSIP').value === null
      ) {
        this.form.get('dealNbr').enable();
      } else {
        this.form.get('dealNbr').disable();
      }
    }

    if (inputName === 'dealNumberField') {
      this.showError.dealNbr = false;
      if (this.form.get('dealNbr').value === '' || this.form.get('dealNbr').value === undefined) {
        this.form.get('CUSIP').enable();
      } else {
        this.form.get('CUSIP').disable();
      }
    }
  }
  public onEnterHit($event): void {
    if ($event.keyCode === 13 && (this.isSearchBtnEnable || this.isTraderSel)) {
      this.onDealSearch();
    }
  }

  public onCusipFocus() {
    this.isCusipFieldFocused = true;
  }

  public resetCusipDealerInputs(inputName: string): void {
    if (
      this.form.get('CUSIP').value !== undefined &&
      this.form.get('CUSIP').value != null &&
      inputName === 'cusipField'
    ) {
      this.isCusipFieldFocused = false;
      if (this.form.get('CUSIP').value.length === 0) {
        this.form.get('CUSIP').markAsPristine();
        this.form.get('dealNbr').enable();
      }
    } else if (
      (this.form.get('CUSIP').value === undefined || this.form.get('CUSIP').value === null) &&
      inputName === 'cusipField'
    ) {
      this.isCusipFieldFocused = false;
      this.form.get('dealNbr').enable();
    }
    if (
      this.form.get('dealNbr').value !== undefined &&
      this.form.get('dealNbr').value != null &&
      inputName === 'dealNumberField'
    ) {
      this.isDealNbrFieldFocused = false;
      if (this.form.get('dealNbr').value.length === 0) {
        this.form.get('dealNbr').markAsPristine();
        this.form.get('CUSIP').enable();
        this.validateSearchNo();
      }
    } else if (this.form.get('dealNbr').value === undefined && inputName === 'dealNumberField') {
      this.isDealNbrFieldFocused = false;
      this.validateSearchNo();
      this.form.get('CUSIP').enable();
    }
  }

  public disableDealerControl(): void {
    this.form.get('dealNbr').disable();
  }

  public disableCUSIPControl(): void {
    this.form.get('CUSIP').disable();
  }

  private validateDealNbr(): ValidatorFn {
    return (control: UntypedFormControl): { [key: string]: boolean } => {
      const dealRegex = new RegExp('^[0-9]{4}[-][0-9]{3}$');
      const dealSMBSRegex = new RegExp('^[0-9]{3}$');
      const dealMultiFamilyRegex = new RegExp('^[0-9]{4}[-][Mm][0-9A-Za-z]{2}$');
      const dealBenchMarkRegex = new RegExp('^[0-9]{4}[-][Bb][0-9]{2}$');
      const dealGrantorTrustRegex = new RegExp('^[0-9]{4}[-][Tt][0-9]{2}$');
      const dealWholeLoanRegex = new RegExp('^[0-9]{4}[-][Ww][0-9]{2}$');
      const dealGinneMaeRegex = new RegExp('^[0-9]{4}[-][Gg][0-9]{2}$');
      const dealSwapTrustRegex = new RegExp('^[0-9]{4}[-][Ss][0-9]{2}$');
      const dealPrivateLableRegex = new RegExp('^[0-9]{4}[-][Pp][0-9]{2}$');
      const dealCallableRegex = new RegExp('^[0-9]{4}[-][Cc][0-9]{2}$');
      const dealDebtDealRegex = new RegExp('^[0-9]{4}[-][Dd][0-9]{2}$');
      const dealRedeemableRegex = new RegExp('^[0-9]{4}[-][Rr][0-9]{2}$');

      if (
        dealRegex.test(control.value) ||
        dealSMBSRegex.test(control.value) ||
        dealMultiFamilyRegex.test(control.value) ||
        dealBenchMarkRegex.test(control.value) ||
        dealGrantorTrustRegex.test(control.value) ||
        dealWholeLoanRegex.test(control.value) ||
        dealGinneMaeRegex.test(control.value) ||
        dealSwapTrustRegex.test(control.value) ||
        dealPrivateLableRegex.test(control.value) ||
        dealCallableRegex.test(control.value) ||
        dealDebtDealRegex.test(control.value) ||
        dealRedeemableRegex.test(control.value)
      ) {
        this.mDealSubType = '';
        this.isSearchBtnEnable = true;
        return null;
      } else {
        this.isSearchBtnEnable = false;
        return { dealerNbrError: false };
      }
    };
  }
  public onDealerFocus() {
    this.isDealNbrFieldFocused = true;
  }
  public resetInput(): void {
    this.form.get('dealNbr').setValue('');
    this.form.get('dealNbr').markAsPristine();
    this.inputDealNbr.nativeElement.focus();
    this.form.get('CUSIP').enable();
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
